import {memo, useRef, type ComponentProps} from 'react';
import {useIntl} from 'react-intl';
import classNames from 'classnames';
import {useTooltipTriggerState} from '@react-stately/tooltip';
import {useTooltipTrigger} from '@react-aria/tooltip';

import Button from '../button';
import {t} from 'utils/i18n';
import {MessageCheckOutlineIcon, MessageMinusOutlineIcon, Tooltip, TooltipTextPrimary} from 'packages/ui-kit';

type Props = {
    isFollowing: boolean;
    shouldOverflow?: boolean;
}

type FollowButtonProps = Props & Exclude<ComponentProps<typeof Button>, Props>;

const TOOLTIP_TRIGGER_STATE_PROPS = {};
const TOOLTIP_TRIGGER_PROPS = {};

const FollowButton: React.FC<FollowButtonProps> = ({isFollowing, shouldOverflow, ...props}) => {
    const {formatMessage} = useIntl();

    let textContent = isFollowing ? formatMessage({
        id: t('threading.following'),
        defaultMessage: 'Following',
    }) : formatMessage({
        id: t('threading.notFollowing'),
        defaultMessage: 'Follow',
    });

    const triggerRef = useRef<HTMLButtonElement>(null);

    const tooltipTriggerState = useTooltipTriggerState(TOOLTIP_TRIGGER_STATE_PROPS);
    const {triggerProps, tooltipProps} = useTooltipTrigger(TOOLTIP_TRIGGER_PROPS, tooltipTriggerState, triggerRef);

    if (!shouldOverflow) {
        return (
            <Button
                {...props}
                className={classNames(props?.className, 'FollowButton')}
                isActive={isFollowing}
            >
                {textContent}
            </Button>
        );
    }

    textContent = isFollowing ? formatMessage({
        id: t('channel_info_rhs.header.unfollowThread'),
        defaultMessage: 'Unfollow thread',
    }) : formatMessage({
        id: t('channel_info_rhs.header.followThread'),
        defaultMessage: 'Follow thread',
    });

    return (
        <>
            <button
                {...props}
                {...triggerProps}
                ref={triggerRef}
                type='button'
                className={classNames(props?.className, 'FollowButton', 'btn-icon', {'btn-icon--active': isFollowing})}
                aria-label={textContent}
            >
                {isFollowing ? (
                    <MessageMinusOutlineIcon width={16} height={16} />
                ) : <MessageCheckOutlineIcon width={16} height={16} />}
            </button>
            {tooltipTriggerState.isOpen && (
                <Tooltip
                    {...tooltipProps}
                    targetRef={triggerRef}
                    state={tooltipTriggerState}
                >
                    <TooltipTextPrimary>{textContent}</TooltipTextPrimary>
                </Tooltip>
            )}
        </>

    );
};

export default memo(FollowButton);
